import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Globe, ShoppingCart, Heart, Scale, Sun, Moon, User } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { useCart } from '../context/CartContext';
import { useWishlist } from '../context/WishlistContext';
import { useCompare } from '../context/CompareContext';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import SearchBar from './SearchBar';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const location = useLocation();
  const { language, setLanguage, toggleLanguage, t } = useLanguage();
  const [isLangMenuOpen, setIsLangMenuOpen] = useState(false);
  const { getCartCount } = useCart();
  const { wishlist } = useWishlist();
  const { compareList } = useCompare();
  const { theme, toggleTheme } = useTheme();
  const { user } = useAuth();

  const navLinks = [
    { name: t.nav.home, path: '/' },
    { name: t.nav.products, path: '/products' },
    { name: t.nav.findByBike, path: language === 'es' ? '/buscar-recambios-por-moto' : language === 'en' ? '/find-parts-by-bike' : '/trova-ricambi-per-moto' },
    { name: t.nav.about, path: '/about' },
    { name: t.nav.whereToBuy, path: '/where-to-buy' },
    { name: t.nav.contact, path: '/contact' }
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <header className="fixed left-0 right-0 bg-black border-b border-white/25 z-50" style={{ top: 'var(--promo-h, 0px)' }}>
      <div className="max-w-[1400px] mx-auto px-6 lg:px-[7.6923%] h-20 flex items-center justify-between">
        <Link to="/" className="flex items-center">
          <img 
            src="/allmx-logo.png" 
            alt="ALLMX" 
            className="h-10 md:h-12 w-auto object-contain"
            style={{ 
              filter: 'hue-rotate(18deg) saturate(1.5) brightness(1.1)'
            }}
          />
        </Link>

        {/* Desktop Navigation */}
        <nav className="hidden lg:flex items-center gap-8">
          {navLinks.map((link) => (
            <Link
              key={link.path}
              to={link.path}
              className="text-lg font-normal transition-colors duration-300"
              style={{
                color: isActive(link.path) ? 'var(--brand-active)' : 'var(--text-muted)',
              }}
              onMouseEnter={(e) => e.target.style.color = 'var(--text-primary)'}
              onMouseLeave={(e) => {
                if (!isActive(link.path)) {
                  e.target.style.color = 'var(--text-muted)';
                }
              }}
            >
              {link.name}
            </Link>
          ))}

          {/* Theme Toggle */}
          <button
            onClick={toggleTheme}
            className="p-2 text-white hover:text-[var(--brand-primary)] transition-colors"
            aria-label={theme === 'dark' ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
          >
            {theme === 'dark' ? <Sun size={22} /> : <Moon size={22} />}
          </button>
        </nav>

        {/* Persistent Search Bar (visible on every page, desktop only) */}
        <div className="hidden lg:block w-72 xl:w-96" data-testid="header-search-bar">
          <SearchBar />
        </div>

        {/* Desktop Actions */}
        <div className="hidden lg:flex items-center gap-4">
          {/* Wishlist Icon */}
          <Link
            to="/wishlist"
            className="relative p-2 text-white hover:text-[var(--brand-primary)] transition-colors"
            title={language === 'it' ? 'Preferiti' : 'Wishlist'}
          >
            <Heart size={24} />
            {wishlist.length > 0 && (
              <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center">
                {wishlist.length}
              </span>
            )}
          </Link>

          {/* Compare Icon */}
          <Link
            to="/compare"
            className="relative p-2 text-white hover:text-[var(--brand-primary)] transition-colors"
            title={language === 'it' ? 'Confronta' : 'Compare'}
          >
            <Scale size={24} />
            {compareList.length > 0 && (
              <span className="absolute -top-1 -right-1 bg-[var(--brand-primary)] text-black text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center">
                {compareList.length}
              </span>
            )}
          </Link>

          {/* Cart Icon */}
          <Link
            to="/cart"
            className="relative p-2 text-white hover:text-[var(--brand-primary)] transition-colors"
            title={language === 'it' ? 'Carrello' : 'Cart'}
          >
            <ShoppingCart size={24} />
            {getCartCount() > 0 && (
              <span className="absolute -top-1 -right-1 bg-[var(--brand-primary)] text-black text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center">
                {getCartCount()}
              </span>
            )}
          </Link>

          {/* Account Icon */}
          <Link
            to={user ? "/account" : "/login"}
            className="relative p-2 text-white hover:text-[var(--brand-primary)] transition-colors"
            title={user ? (language === 'it' ? 'Il Mio Account' : 'My Account') : (language === 'it' ? 'Accedi' : 'Sign In')}
            data-testid="account-icon"
          >
            <User size={24} />
            {user && (
              <span className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 bg-green-500 rounded-full border-2" style={{ borderColor: 'var(--bg-primary)' }} />
            )}
          </Link>

          {/* Language Switcher Dropdown */}
          <div className="relative">
            <button
              onClick={() => setIsLangMenuOpen(!isLangMenuOpen)}
              onBlur={() => setTimeout(() => setIsLangMenuOpen(false), 200)}
              className="flex items-center gap-2 px-4 py-2 text-white hover:text-[var(--brand-primary)] transition-colors"
              title="Select language"
              data-testid="language-selector-btn"
            >
              <Globe size={20} />
              <span className="font-semibold uppercase">{language}</span>
            </button>
            {isLangMenuOpen && (
              <div
                className="absolute right-0 mt-2 w-32 bg-black border border-white/25 rounded shadow-lg z-50"
                data-testid="language-dropdown"
              >
                {[
                  { code: 'it', label: 'Italiano', flag: '🇮🇹' },
                  { code: 'en', label: 'English', flag: '🇬🇧' },
                  { code: 'es', label: 'Español', flag: '🇪🇸' }
                ].map((lang) => (
                  <button
                    key={lang.code}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      setLanguage(lang.code);
                      setIsLangMenuOpen(false);
                    }}
                    className={`w-full flex items-center gap-2 px-3 py-2 text-left text-sm hover:bg-white/10 transition-colors ${
                      language === lang.code ? 'text-[var(--brand-primary)] font-semibold' : 'text-white'
                    }`}
                    data-testid={`language-option-${lang.code}`}
                  >
                    <span>{lang.flag}</span>
                    <span>{lang.label}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Mobile Actions - Cart Icon + Menu Button */}
        <div className="lg:hidden flex items-center gap-3">
          {/* Cart Icon for Mobile */}
          <Link
            to="/cart"
            className="relative text-white p-2 hover:text-[var(--brand-primary)] transition-colors"
          >
            <ShoppingCart size={24} />
            {getCartCount() > 0 && (
              <span className="absolute -top-1 -right-1 bg-[var(--brand-primary)] text-black text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center">
                {getCartCount()}
              </span>
            )}
          </Link>

          {/* Mobile Menu Button */}
          <button
            className="text-white p-2 rounded hover:bg-white/10 transition-colors"
            style={{ 
              backgroundColor: 'rgba(0, 0, 0, 0.6)',
              backdropFilter: 'blur(4px)'
            }}
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            aria-label="Menu"
          >
            {isMenuOpen ? <X size={28} strokeWidth={2.5} /> : <Menu size={28} strokeWidth={2.5} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMenuOpen && (
        <div className="lg:hidden bg-black border-t border-white/25">
          <nav className="flex flex-col px-6 py-4">
            {/* Mobile persistent search bar */}
            <div className="pb-3 border-b border-white/10 mb-2" data-testid="mobile-menu-search">
              <SearchBar />
            </div>
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className="py-3 text-lg border-b border-white/10"
                style={{
                  color: isActive(link.path) ? 'var(--brand-primary)' : 'var(--text-secondary)',
                }}
                onClick={() => setIsMenuOpen(false)}
              >
                {link.name}
              </Link>
            ))}
            
            {/* Mobile Action Icons - Wishlist, Compare, Cart */}
            <div className="flex items-center gap-6 py-4 border-b border-white/10">
              <Link
                to="/wishlist"
                className="relative flex items-center gap-2 text-white"
                onClick={() => setIsMenuOpen(false)}
              >
                <Heart size={24} />
                <span className="text-lg">{language === 'it' ? 'Preferiti' : 'Wishlist'}</span>
                {wishlist.length > 0 && (
                  <span className="bg-red-500 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center">
                    {wishlist.length}
                  </span>
                )}
              </Link>
            </div>

            <div className="flex items-center gap-6 py-4 border-b border-white/10">
              <Link
                to="/compare"
                className="relative flex items-center gap-2 text-white"
                onClick={() => setIsMenuOpen(false)}
              >
                <Scale size={24} />
                <span className="text-lg">{language === 'it' ? 'Confronta' : 'Compare'}</span>
                {compareList.length > 0 && (
                  <span className="bg-blue-500 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center">
                    {compareList.length}
                  </span>
                )}
              </Link>
            </div>

            <div className="flex items-center gap-6 py-4 border-b border-white/10">
              <Link
                to="/cart"
                className="relative flex items-center gap-2 text-white"
                onClick={() => setIsMenuOpen(false)}
              >
                <ShoppingCart size={24} />
                <span className="text-lg">{language === 'it' ? 'Carrello' : 'Cart'}</span>
                {getCartCount() > 0 && (
                  <span className="bg-[var(--brand-primary)] text-black text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center">
                    {getCartCount()}
                  </span>
                )}
              </Link>
            </div>

            {/* Mobile Account / Login - missing from header on small screens */}
            <div className="flex items-center gap-6 py-4 border-b border-white/10">
              <Link
                to={user ? '/account' : '/login'}
                className="relative flex items-center gap-2 text-white"
                onClick={() => setIsMenuOpen(false)}
                data-testid="mobile-account-link"
              >
                <User size={24} />
                <span className="text-lg">
                  {user
                    ? (language === 'it' ? 'Il Mio Account' : language === 'es' ? 'Mi Cuenta' : 'My Account')
                    : (language === 'it' ? 'Accedi / Registrati' : language === 'es' ? 'Iniciar sesión / Registrarse' : 'Sign in / Sign up')}
                </span>
                {user && (
                  <span className="w-2.5 h-2.5 bg-green-500 rounded-full ml-1" />
                )}
              </Link>
            </div>
            
            {/* Mobile Language Switcher */}
            <div className="py-3 border-b border-white/10">
              <div className="flex items-center gap-2 text-white mb-2">
                <Globe size={20} />
                <span className="text-lg">Language / Lingua / Idioma</span>
              </div>
              <div className="flex gap-2 mt-2" data-testid="mobile-language-selector">
                {[
                  { code: 'it', flag: '🇮🇹', label: 'IT' },
                  { code: 'en', flag: '🇬🇧', label: 'EN' },
                  { code: 'es', flag: '🇪🇸', label: 'ES' }
                ].map((lang) => (
                  <button
                    key={lang.code}
                    onClick={() => {
                      setLanguage(lang.code);
                      setIsMenuOpen(false);
                    }}
                    className={`flex items-center gap-1 px-3 py-2 rounded border ${
                      language === lang.code
                        ? 'bg-[var(--brand-primary)] text-black border-[var(--brand-primary)] font-bold'
                        : 'border-white/30 text-white hover:bg-white/10'
                    }`}
                    data-testid={`mobile-language-option-${lang.code}`}
                  >
                    <span>{lang.flag}</span>
                    <span>{lang.label}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Mobile Theme Toggle */}
            <button
              onClick={toggleTheme}
              className="py-3 text-lg border-b border-white/10 flex items-center gap-2 text-white"
            >
              {theme === 'dark' ? <Sun size={20} /> : <Moon size={20} />}
              <span>{theme === 'dark' ? (language === 'it' ? 'Tema Chiaro' : 'Light Mode') : (language === 'it' ? 'Tema Scuro' : 'Dark Mode')}</span>
            </button>

            <div className="mt-4">
              <a
                href="https://www.amazon.it/s?me=A3526ZZLQNVU1W&marketplaceID=APJ6JRA9NG5V4"
                target="_blank"
                rel="noopener noreferrer"
                className="btn-primary w-full"
                onClick={() => setIsMenuOpen(false)}
              >
                {t.nav.shopNow}
              </a>
            </div>
          </nav>
        </div>
      )}
    </header>
  );
};

export default Header;
